import { useState } from 'react'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import type { Leave } from 'modules/shift_management/lib/types'
import EditLeave from 'modules/timeoff/components/EditLeave'
import LeaveRecommendationModal from 'modules/timeoff/components/LeaveRecommendationModal'
import RejectLeave from 'modules/timeoff/components/RejectLeave'
import { useLeaveRecommendationFeatureFlag } from 'modules/timeoff/hooks/useLeaveRecommendationFeatureFlag'
import { LeavesContextProvider } from 'modules/timeoff/lib/LeavesContextProvider'
import timeoffAllowances from 'modules/timeoff/store/collections/allowances'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import policyTimelines from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'

interface Props {
  leave: Leave
  contractVersions: ContractVersions
  onClose: () => void
}

const LeaveModal = ({ leave, contractVersions, onClose }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const { loading } = useAsyncData(async () => {
    await leaveTypes.fetch()
    await timeoffAllowances.fetch()

    if (hasFeatureFlag(ShiftManagementFeatureFlags.TIMEOFF_POLICY_TIMELINE)) {
      await policyTimelines.fetch({
        data: {
          employee_id: leave.employeeId,
        },
      })
    }
  }, [])

  const [isRejecting, setIsRejecting] = useState(false)

  const { loading: ffLoading, enabled: isRecommendationEnabled } =
    useLeaveRecommendationFeatureFlag()

  const showRecommendationModal = !ffLoading && isRecommendationEnabled

  return (
    <LeavesContextProvider>
      {!isRejecting &&
        (showRecommendationModal ? (
          <LeaveRecommendationModal
            loading={loading}
            contractVersions={contractVersions}
            leaveId={leave.id}
            onClose={onClose}
            onChange={onClose}
            onReject={() => setIsRejecting(true)}
            fromShifts
          />
        ) : (
          <EditLeave
            loading={loading}
            contractVersions={contractVersions}
            leaveId={leave.id}
            onClose={onClose}
            onChange={onClose}
            onReject={() => setIsRejecting(true)}
            fromShifts
          />
        ))}
      {isRejecting && <RejectLeave leaveId={leave.id} onClose={onClose} fromShifts />}
    </LeavesContextProvider>
  )
}

export default observer(LeaveModal)
